import { createSlice } from '@reduxjs/toolkit';
import { fetchStatuses } from '../../../../constants/fetchStatuses';
import {
  createCompanyGroup,
  deleteCompanyGroup,
  fetchCompanyGroup,
  fetchCompanyGroups,
  fetchPortfolios,
  updateCompanyGroup,
  uploadCompanyGroupsCsv,
} from './companyGroup.actions';
import { CompanyGroupState } from './companyGroup.types';

const initialState: CompanyGroupState = {
  companyGroups: [],
  totalCompanyGroups: 0,
  selectedCompanyGroup: null,
  fetchCompanyGroupsStatus: fetchStatuses.idle,
  fetchCompanyGroupStatus: fetchStatuses.idle,
  createCompanyGroupStatus: fetchStatuses.idle,
  updateCompanyGroupStatus: fetchStatuses.idle,
  deleteCompanyGroupStatus: fetchStatuses.idle,
  uploadCompanyGroupsCsvStatus: fetchStatuses.idle,
  fetchPortfoliosStatus: fetchStatuses.idle,
  portfolios: [],
  totalPortfolios: 0,
  error: null,
};

const companyGroupSlice = createSlice({
  name: 'companyGroup',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // === FETCH COMPANY GROUPS ===
    builder.addCase(fetchCompanyGroups.pending, (state) => {
      state.error = null;
      state.fetchCompanyGroupsStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCompanyGroups.fulfilled, (state, action) => {
      state.fetchCompanyGroupsStatus = fetchStatuses.success;
      state.companyGroups = action.payload.data?.companyGroups ?? [];
      state.totalCompanyGroups = action.payload.data?.total ?? 0;
    });
    builder.addCase(fetchCompanyGroups.rejected, (state, action) => {
      state.fetchCompanyGroupsStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH COMPANY GROUP ===
    builder.addCase(fetchCompanyGroup.pending, (state) => {
      state.error = null;
      state.fetchCompanyGroupStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchCompanyGroup.fulfilled, (state, action) => {
      state.fetchCompanyGroupStatus = fetchStatuses.success;
      state.selectedCompanyGroup = action.payload.data ?? null;
    });
    builder.addCase(fetchCompanyGroup.rejected, (state, action) => {
      state.fetchCompanyGroupStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === CREATE COMPANY GROUP ===
    builder.addCase(createCompanyGroup.pending, (state) => {
      state.error = null;
      state.createCompanyGroupStatus = fetchStatuses.pending;
    });
    builder.addCase(createCompanyGroup.fulfilled, (state) => {
      state.createCompanyGroupStatus = fetchStatuses.success;
    });
    builder.addCase(createCompanyGroup.rejected, (state, action) => {
      state.createCompanyGroupStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPDATE COMPANY GROUP ===
    builder.addCase(updateCompanyGroup.pending, (state) => {
      state.error = null;
      state.updateCompanyGroupStatus = fetchStatuses.pending;
    });
    builder.addCase(updateCompanyGroup.fulfilled, (state) => {
      state.updateCompanyGroupStatus = fetchStatuses.success;
    });
    builder.addCase(updateCompanyGroup.rejected, (state, action) => {
      state.updateCompanyGroupStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === DELETE COMPANY GROUP ===
    builder.addCase(deleteCompanyGroup.pending, (state) => {
      state.error = null;
      state.deleteCompanyGroupStatus = fetchStatuses.pending;
    });
    builder.addCase(deleteCompanyGroup.fulfilled, (state) => {
      state.deleteCompanyGroupStatus = fetchStatuses.success;
    });
    builder.addCase(deleteCompanyGroup.rejected, (state, action) => {
      state.deleteCompanyGroupStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === UPLOAD COMPANY GROUPS CSV ===
    builder.addCase(uploadCompanyGroupsCsv.pending, (state) => {
      state.error = null;
      state.uploadCompanyGroupsCsvStatus = fetchStatuses.pending;
    });
    builder.addCase(uploadCompanyGroupsCsv.fulfilled, (state) => {
      state.uploadCompanyGroupsCsvStatus = fetchStatuses.success;
    });
    builder.addCase(uploadCompanyGroupsCsv.rejected, (state, action) => {
      state.uploadCompanyGroupsCsvStatus = fetchStatuses.rejected;
      state.error = action.error;
    });

    // === FETCH PORTFOLIOS ===
    builder.addCase(fetchPortfolios.pending, (state) => {
      state.error = null;
      state.fetchPortfoliosStatus = fetchStatuses.pending;
    });
    builder.addCase(fetchPortfolios.fulfilled, (state, action) => {
      state.fetchPortfoliosStatus = fetchStatuses.success;
      state.portfolios = action.payload.data?.items ?? [];
      state.totalPortfolios = action.payload.data?.totalItems ?? 0;
    });
    builder.addCase(fetchPortfolios.rejected, (state, action) => {
      state.fetchPortfoliosStatus = fetchStatuses.rejected;
      state.error = action.error;
    });
  },
});

export default companyGroupSlice.reducer;
